import React, { createContext, useState, useEffect } from "react";

export const ThemeContext = createContext();

// Outlook-style theme — blue and white, no black/near-black backgrounds
// anywhere. "light" and "dark" are kept as two variants (so the existing
// toggle keeps working) but both stay within the blue/white palette; "dark"
// just leans a little more toward the light-blue tint instead of pure white.
export const lightTheme = {
  name: "light",
  bg: {
    primary: "#ffffff",
    secondary: "#f3f2f1",
    tertiary: "#eff6fc",
    dark: "#deecf9",
  },
  text: {
    primary: "#201f1e",
    secondary: "#3b3a39",
    tertiary: "#605e5c",
    light: "#8a8886",
  },
  border: "#e1dfdd",
  accent: "#0078d4",
  statusColors: {
    pending: "#ca5010",
    approved: "#107c10",
    executed: "#0078d4",
    inreview: "#8764b8",
    expired: "#a4262c",
    cancelled: "#6a6a6a",
  },
  hover: "rgba(0, 120, 212, 0.08)",
};

export const darkTheme = {
  name: "dark",
  bg: {
    primary: "#f3f9fd",
    secondary: "#eff6fc",
    tertiary: "#deecf9",
    dark: "#c7e0f4",
  },
  text: {
    primary: "#201f1e",
    secondary: "#3b3a39",
    tertiary: "#605e5c",
    light: "#c7e0f4",
  },
  border: "#c7e0f4",
  accent: "#0078d4",
  statusColors: {
    pending: "#ca5010",
    approved: "#107c10",
    executed: "#0078d4",
    inreview: "#8764b8",
    expired: "#a4262c",
    cancelled: "#6a6a6a",
  },
  hover: "rgba(0, 120, 212, 0.1)",
};

export const ThemeProvider = ({ children }) => {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem("theme");
    if (saved) {
      setIsDark(saved === "dark");
    } else {
      setIsDark(false);
    }
  }, []);

  useEffect(() => {
    // Apply dark class to document root for Tailwind dark mode
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);

  const toggleTheme = () => {
    setIsDark(prev => {
      const newValue = !prev;
      localStorage.setItem("theme", newValue ? "dark" : "light");
      document.documentElement.classList.toggle('dark', newValue);
      return newValue;
    });
  };

  const theme = isDark ? darkTheme : lightTheme;

  return (
    <ThemeContext.Provider value={{ theme, isDark, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = React.useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
};

// Helper to replace hardcoded dark colors with theme colors
export const getDynamicColor = (theme, isDark, color) => {
  const colorMap = {
    "#0d1e35": theme.bg.secondary,
    "#0a1628": theme.bg.tertiary,
    "#111f38": theme.bg.secondary,
    "#1e3054": theme.border,
    "#8fa3c0": theme.text.secondary,
    "#f0ede8": theme.text.primary,
    "#c8c4bc": theme.text.tertiary,
    "#060e1f": theme.bg.primary,
    "#c9a84c": "#0078d4",
  };
  return colorMap[color] || color;
};
